import { useState, useEffect, useRef } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { Send, MessageSquare, Shield, Truck, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function AdminLoadChat({ load, onClose }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  // user.full_name comes from Supabase Auth metadata, which is frequently
  // never set -- an admin/dispatcher's real name lives on their
  // StaffProfile instead. Without this, a chat message shows their email
  // as the sender to the driver on the other end.
  const [staffFullName, setStaffFullName] = useState(null);

  useEffect(() => {
    if (!user?.email) return;
    client.entities.StaffProfile.filter({ user_email: user.email }, '-updated_date', 1)
      .then(rows => setStaffFullName(rows[0]?.full_name || null))
      .catch(() => setStaffFullName(null));
  }, [user?.email]);

  useEffect(() => {
    if (!load?.id) return;
    fetchMessages();

    const unsubscribe = client.entities.LoadMessage.subscribe((event) => {
      if (event.data?.load_id !== load.id) return;
      if (event.type === 'create') {
        setMessages(prev => prev.some(m => m.id === event.data.id) ? prev : [...prev, event.data]);
      }
    });

    return () => unsubscribe();
  }, [load?.id]);

  // Scrolls only the chat's own message list. scrollIntoView on the
  // bottom sentinel also scrolls every ancestor -- i.e. the whole Active
  // Load page -- so opening the page jumped straight down to the chat.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    const data = await client.entities.LoadMessage.filter({ load_id: load.id }, 'created_date', 100);
    setMessages(data);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    setSending(true);
    setInput('');
    const saved = await client.entities.LoadMessage.create({
      load_id: load.id,
      load_number: load.load_number,
      sender_email: user.email,
      sender_name: staffFullName || user.full_name || user.email,
      sender_role: 'admin',
      message: text,
    });
    // Optimistically add to local state; dedupe in case subscription also fires
    setMessages(prev => prev.some(m => m.id === saved.id) ? prev : [...prev, saved]);
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isMe = (msg) => msg.sender_email === user.email;
  const isAdmin = (msg) => msg.sender_role === 'admin';

  return (
    <div className="glass-card flex flex-col border-primary/20" style={{ height: '500px' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
        <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
          <MessageSquare className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">
            Load #{load.load_number} — {load.origin} → {load.destination}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {load.assigned_driver_name || 'No driver assigned'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs text-muted-foreground">Live</span>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground ml-2">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageSquare className="w-8 h-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No messages yet.</p>
            <p className="text-xs text-muted-foreground mt-0.5">Start the conversation with the driver.</p>
          </div>
        )}
        {messages.map((msg) => {
          const mine = isMe(msg);
          const admin = isAdmin(msg);
          return (
            <div key={msg.id} className={cn("flex gap-2", mine ? "justify-end" : "justify-start")}>
              {!mine && (
                <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Truck className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
              )}
              <div className={cn("max-w-[75%] flex flex-col gap-0.5", mine ? "items-end" : "items-start")}>
                {!mine && (
                  <span className="text-xs text-muted-foreground px-1">{msg.sender_name}</span>
                )}
                <div className={cn(
                  "px-3.5 py-2 rounded-2xl text-sm leading-relaxed",
                  mine
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-white/8 border border-border text-foreground rounded-bl-sm"
                )}>
                  {msg.message}
                </div>
                <span className="text-xs text-muted-foreground px-1">
                  {new Date(msg.created_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-border flex gap-2">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message driver… (Enter to send)"
          rows={1}
          className="flex-1 px-3 py-2 rounded-xl bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary/50"
        />
        <button
          onClick={handleSend}
          disabled={sending || !input.trim()}
          className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 hover:bg-primary/90 disabled:opacity-40 transition-colors self-end"
        >
          <Send className="w-4 h-4 text-primary-foreground" />
        </button>
      </div>
    </div>
  );
}